import React, { useState, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getMergedUserData } from "../pages/const";
import {
  postSalesReturn,
  isErpApiSuccess,
  generateIdempotencyKey,
  getErpRequestErrorMessage,
} from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import {
  buildStandardPaymentLines,
  isBanklessPayMode,
  PAYMENT_METHOD_LABELS,
} from "../utils/paymentLinesErpAdapter";
import { toast } from "react-hot-toast";

import Select from "react-select";

const AUTO_TYPE_PAY_MODES = [
  "cash",
  "wallet",
  "Wallet",
  "kasar",
  "old_gold",
  "old_silver",
];

const findBankAccountForPayMode = (payMode, accounts = []) => {
  if (!payMode) return null;
  const keyword = String(payMode).toLowerCase().replace(/_/g, " ");
  return (
    accounts.find((bank) => {
      const accountType = String(
        bank.account_type || bank.accountType || "",
      ).toLowerCase();
      const bankName = String(
        bank.bank_name || bank.bankName || "",
      ).toLowerCase();
      return (
        accountType === keyword ||
        accountType === String(payMode).toLowerCase() ||
        bankName.includes(keyword)
      );
    }) || null
  );
};

const resolveBankAccountId = (typeValue) => {
  if (typeValue === null || typeValue === undefined || typeValue === "") {
    return null;
  }
  const parsed = parseInt(String(typeValue), 10);
  if (
    !Number.isNaN(parsed) &&
    parsed > 0 &&
    String(parsed) === String(typeValue).trim()
  ) {
    return parsed;
  }
  return null;
};

const resolveRowBankAccountId = (row, accounts = [], typeOptions = []) => {
  const fromType = resolveBankAccountId(row?.type);
  if (fromType) return fromType;
  if (isBanklessPayMode(row?.pay_mode)) return null;

  const payMode = String(row?.pay_mode || "").toLowerCase();
  const typeRaw = String(row?.type || "").toLowerCase();

  // Cash UI locks Type to "cash" — resolve the Cash ledger from accounts / paymentTypes
  if (payMode === "cash" || typeRaw === "cash") {
    const cashAccount = findBankAccountForPayMode("cash", accounts);
    if (cashAccount?.id != null) return Number(cashAccount.id);
    const cashOption = (typeOptions || []).find((opt) => {
      const label = String(opt.label || "").toLowerCase().trim();
      return label === "cash" || label.includes("cash");
    });
    if (cashOption?.value != null) {
      const n = Number(cashOption.value);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }

  const account = findBankAccountForPayMode(row?.pay_mode, accounts);
  return account?.id != null ? Number(account.id) : null;
};

// Payment method options
const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "Wallet", label: "Wallet" },
  { value: "old_gold", label: "Old Gold" },
  { value: "old_silver", label: "Old Silver" },
  { value: "kasar", label: "Kasar" },
  { value: "scheme", label: "Scheme" },
  { value: "order", label: "Order" },
  { value: "os", label: "OS" },
];

function ReturnModal({
  show,
  onHide,
  invoice,
  users,
  bankAccountsList = [],
  paymentTypes = [],
  onSuccess,
}) {
  const navigate = useNavigate();
  const [returnStep, setReturnStep] = useState(1);
  const [returnPaymentRows, setReturnPaymentRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "cash",
      remarks: "",
      amount: "0.00",
    },
    {
      id: 2,
      pay_mode: "cheque",
      type: "",
      remarks: "",
      amount: "0.00",
    },
    {
      id: 3,
      pay_mode: "bank_transfer",
      type: "",
      remarks: "",
      amount: "0.00",
    },
  ]);
  const [returnDate, setReturnDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [returnRemarks, setReturnRemarks] = useState("");
  const [returnReasons, setReturnReasons] = useState({});
  const [returnLoading, setReturnLoading] = useState(false);
  const [itemsToSaleCount, setItemsToSaleCount] = useState(1);
  const [saleSelection, setSaleSelection] = useState({});
  const [returnType, setReturnType] = useState("fully"); // "partially" or "fully"
  // Item keys selected to RETURN in a partial return (multi-select).
  // Prefer sale item id — tag_no can be blank on some bills.
  const [selectedItemKeysForPartial, setSelectedItemKeysForPartial] = useState(
    [],
  );
  const userData = getMergedUserData();

  const getReturnItemKey = (item, index = 0) => {
    if (item?.id != null && item.id !== "") return `id:${item.id}`;
    const tag = item?.tag_no || item?.tagNo || item?.tag || "";
    if (tag) return `tag:${tag}`;
    return `idx:${index}`;
  };

  // Listen for API URL changes and close modal
  React.useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in ReturnModal, closing modal...', event.detail);
      if (show) {
        toast('Database switched. Closing modal.');
        onHide();
      }
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [show, onHide]);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (show && invoice) {
      setReturnStep(1);
      setReturnReasons({});
      setReturnPaymentRows([
        {
          id: 1,
          pay_mode: "cash",
          type: "cash",
          remarks: "",
          amount: "0.00",
        },
        {
          id: 2,
          pay_mode: "cheque",
          type: "",
          remarks: "",
          amount: "0.00",
        },
        {
          id: 3,
          pay_mode: "bank_transfer",
          type: "",
          remarks: "",
          amount: "0.00",
        },
      ]);
      setReturnDate(new Date().toISOString().split("T")[0]);
      setReturnRemarks("");
      setSelectedItemKeysForPartial([]);

      // If single product, auto-select "fully"
      if (invoice.items && invoice.items.length === 1) {
        setReturnType("fully");
      } else {
        setReturnType("fully"); // Default to fully
      }

      // initialize sale selection map (all not selected for sale initially)
      const selection = {};
      (invoice.items || []).forEach((it) => {
        selection[it.id] = false;
      });
      setSaleSelection(selection);
    }
  }, [show, invoice]);

  // Keep cash Type locked to "cash"; resolve bank ids only for non-cash auto modes
  React.useEffect(() => {
    if (!show) return;

    setReturnPaymentRows((prevRows) => {
      let changed = false;
      const nextRows = prevRows.map((row) => {
        if (String(row.pay_mode).toLowerCase() === "cash") {
          if (row.type !== "cash") {
            changed = true;
            return { ...row, type: "cash" };
          }
          return row;
        }
        if (row.type) return row;
        if (!AUTO_TYPE_PAY_MODES.includes(row.pay_mode)) return row;
        if (isBanklessPayMode(row.pay_mode)) return row;
        if (!bankAccountsList?.length) return row;
        const account = findBankAccountForPayMode(
          row.pay_mode,
          bankAccountsList,
        );
        if (!account?.id) return row;
        changed = true;
        return { ...row, type: String(account.id) };
      });
      return changed ? nextRows : prevRows;
    });
  }, [show, bankAccountsList]);

  // Handle return payment row changes
  const handleReturnPaymentRowChange = (rowId, field, value) => {
    setReturnPaymentRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) return row;

        const updatedRow = { ...row, [field]: value };
        if (field === "pay_mode") {
          if (String(value).toLowerCase() === "cash") {
            // Cash mode: Type is always Cash (no bank list)
            updatedRow.type = "cash";
          } else if (isBanklessPayMode(value)) {
            updatedRow.type = "";
          } else {
            // Other modes: clear Type so user picks from bank list
            updatedRow.type = "";
          }
        }
        return updatedRow;
      }),
    );
  };

  // Calculate return payment total
  const returnPaymentTotal = returnPaymentRows.reduce((sum, row) => {
    return sum + parseFloat(row.amount || 0);
  }, 0);

  const togglePartialReturnItem = (itemKey) => {
    if (!itemKey) return;
    setSelectedItemKeysForPartial((prev) =>
      prev.includes(itemKey)
        ? prev.filter((k) => k !== itemKey)
        : [...prev, itemKey],
    );
  };

  const RETURN_GST_PERCENT = 3;

  // Subtotal of items being returned (checked tags in partial mode)
  const returnItemsSubtotal = useMemo(() => {
    if (!invoice) return 0;

    // If partially, sum selected (returned) items only
    if (returnType === "partially" && selectedItemKeysForPartial.length > 0) {
      return (invoice.items || [])
        .filter((item, idx) =>
          selectedItemKeysForPartial.includes(getReturnItemKey(item, idx)),
        )
        .reduce((sum, item) => {
          return sum + parseFloat(item.totalamt || item.amount || 0);
        }, 0);
    }

    // If fully, sum all items
    return (invoice.items || []).reduce((sum, item) => {
      return sum + parseFloat(item.totalamt || item.amount || 0);
    }, 0);
  }, [invoice, returnType, selectedItemKeysForPartial]);

  // Final Amount = selected return tags subtotal + 3% GST (rounded to whole ₹)
  const finalReturnAmount = useMemo(() => {
    const gstAmount = returnItemsSubtotal * (RETURN_GST_PERCENT / 100);
    return Math.round(returnItemsSubtotal + gstAmount);
  }, [returnItemsSubtotal]);

  // Max refundable / payment cap (includes GST, whole ₹)
  const totalReturnAmount = finalReturnAmount;

  // Amount already paid on the original bill (whole ₹)
  const billPaidAmount = useMemo(() => {
    if (!invoice) return 0;
    const explicit = parseFloat(
      invoice.paid_amount ?? invoice.paidAmount ?? "",
    );
    if (Number.isFinite(explicit) && explicit >= 0) {
      return Math.round(explicit);
    }

    const grand = parseFloat(
      invoice.grandTotal ||
        invoice.grand_total ||
        invoice.final_amount ||
        0,
    );
    const remaining = parseFloat(
      invoice.remainingAmount || invoice.remaining_amount || 0,
    );
    return Math.round(
      Math.max(0, grand - (Number.isFinite(remaining) ? remaining : 0)),
    );
  }, [invoice]);

  const billRemainingAmount = useMemo(() => {
    if (!invoice) return 0;
    return Math.round(
      parseFloat(
        invoice.remainingAmount || invoice.remaining_amount || 0,
      ) || 0,
    );
  }, [invoice]);

  const returnAmountDue = useMemo(() => {
    return Math.max(
      0,
      Math.round(finalReturnAmount - billRemainingAmount - returnPaymentTotal),
    );
  }, [finalReturnAmount, billRemainingAmount, returnPaymentTotal]);

  const resolveByUser = () =>
    userData?.id ?? userData?.ua_id ?? userData?.user_id ?? null;

  const customerDisplay = useMemo(() => {
    if (!invoice) {
      return { name: "N/A", mobile: "N/A" };
    }
    const userKey = invoice.userId ?? invoice.user_id ?? invoice.customer_id;
    const fromMap =
      (userKey != null && users?.[userKey]) ||
      (userKey != null && users?.[String(userKey)]) ||
      null;

    const name =
      invoice.customerName ||
      invoice.customer_name ||
      invoice.name ||
      fromMap?.name ||
      "N/A";
    const mobile =
      invoice.customerMobile ||
      invoice.customer_mobile ||
      invoice.mobile ||
      fromMap?.mobile ||
      "N/A";

    return { name, mobile };
  }, [invoice, users]);

  const allInvoiceItems = invoice?.items || [];

  const itemsBeingReturned = useMemo(() => {
    if (returnType === "partially" && selectedItemKeysForPartial.length > 0) {
      return allInvoiceItems.filter((item, idx) =>
        selectedItemKeysForPartial.includes(getReturnItemKey(item, idx)),
      );
    }
    return allInvoiceItems;
  }, [allInvoiceItems, returnType, selectedItemKeysForPartial]);

  // Unchecked tags remain on the original bill (partial mode only)
  const itemsBeingKept = useMemo(() => {
    if (returnType !== "partially") return [];
    return allInvoiceItems.filter(
      (item, idx) =>
        !selectedItemKeysForPartial.includes(getReturnItemKey(item, idx)),
    );
  }, [allInvoiceItems, returnType, selectedItemKeysForPartial]);

  const buildReturnPayments = () => {
    const rowsForApi = returnPaymentRows.map((row) => {
      const bankId = resolveRowBankAccountId(
        row,
        bankAccountsList,
        paymentTypes,
      );
      return {
        ...row,
        type: bankId != null ? String(bankId) : row.type,
        bank_account_id: bankId || undefined,
      };
    });

    return buildStandardPaymentLines(rowsForApi, {
      paymentTypes:
        paymentTypes?.length > 0
          ? paymentTypes
          : (bankAccountsList || []).map((bank) => ({
              value: bank.id,
              label: bank.bank_name || bank.bankName || "",
            })),
      payModeLabels: PAYMENT_METHOD_LABELS,
      createKey: generateIdempotencyKey,
      requireBankAccount: true,
    });
  };

  const validateReturnPayments = (erpPayments) => {
    const rowsWithAmount = returnPaymentRows.filter(
      (row) => parseFloat(row.amount || 0) > 0,
    );
    if (rowsWithAmount.length === 0) return true;

    for (const row of rowsWithAmount) {
      if (isBanklessPayMode(row.pay_mode)) continue;
      const bankId = resolveRowBankAccountId(
        row,
        bankAccountsList,
        paymentTypes,
      );
      if (!bankId) {
        toast.error("Please select a valid cash/bank account for refund");
        return false;
      }
    }

    if (erpPayments.length === 0 && rowsWithAmount.length > 0) {
      toast.error("Please select a valid cash/bank account for refund");
      return false;
    }
    return true;
  };

  // Submit return API for a list of items (stock restore + optional refund payments)
  const submitReturns = async (items) => {
    const byUser = resolveByUser();
    if (!byUser) {
      toast.error("User not authenticated");
      return false;
    }

    const branchId = getSessionBranchId();
    if (branchId == null) {
      toast.error("Branch not found for current session");
      return false;
    }

    if (!items?.length) {
      toast.error("No items available to return");
      return false;
    }

    const erpPayments = buildReturnPayments();
    if (!validateReturnPayments(erpPayments)) {
      return false;
    }

    const pickReturnErrorMessage = (res) =>
      (typeof res?.message === "string" && res.message.trim()) ||
      (typeof res?.error === "string" && res.error.trim()) ||
      (typeof res?.data?.message === "string" && res.data.message.trim()) ||
      (typeof res?.data?.error === "string" && res.data.error.trim()) ||
      null;

    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      const idempotencyKey = generateIdempotencyKey();
      const payload = {
        branch_id: branchId,
        sale_document_id: Number(invoice.id),
        tag_no: item.tag_no || "",
        sale_item_id: item.id ? Number(item.id) : undefined,
        idempotency_key: idempotencyKey,
        date: returnDate,
        reason: returnReasons[item.id] || returnRemarks || "",
        by_user: byUser,
        remarks: returnRemarks || "",
      };
      if (!payload.sale_item_id) delete payload.sale_item_id;

      // Attach refund payments once on the first item to avoid double-refund
      if (index === 0 && erpPayments.length > 0) {
        payload.payments = erpPayments;
      }

      try {
        const res = await postSalesReturn(payload, { idempotencyKey });
        if (!(res?.message === "radha" || isErpApiSuccess(res))) {
          console.error("sale_return failed", { payload, res });
          toast.error(
            pickReturnErrorMessage(res) || "Failed to submit return",
          );
          return false;
        }
      } catch (error) {
        console.error("sale_return request error", { payload, error });
        toast.error(
          getErpRequestErrorMessage(error, "Failed to submit return"),
        );
        return false;
      }
    }

    return true;
  };

  // After successful return, open Edit Invoice for the same sales bill
  const openEditInvoiceForSale = () => {
    const saleId = invoice?.id;
    if (saleId == null) return;

    const voucherNumber =
      invoice.voucherNumber ||
      invoice.voucher_number ||
      invoice.invoice_no ||
      "";

    navigate("/editinvoice", {
      state: {
        selectedItemIds: [saleId],
        selectedInvoices: [
          {
            id: saleId,
            voucherNumber,
          },
        ],
        id: saleId,
        saleId,
        voucherNumber,
      },
    });
  };

  // Handle return step 2 save
  const handleReturnSave = async () => {
    if (!invoice) return;

    const items = invoice.items || [];

    // Partial: return only selected tags to stock; rest stay on the same bill number
    if (returnType === "partially" && selectedItemKeysForPartial.length > 0) {
      const itemsToReturn = items.filter((item, idx) =>
        selectedItemKeysForPartial.includes(getReturnItemKey(item, idx)),
      );

      setReturnLoading(true);
      try {
        const success = await submitReturns(itemsToReturn);
        if (success) {
          toast.success(
            `${itemsToReturn.length} item(s) returned to stock. Opening bill ${invoice.voucherNumber || ""} for edit.`.trim(),
          );
          onHide();
          if (onSuccess) onSuccess();
          openEditInvoiceForSale();
        }
        // submitReturns already toasts on failure
      } catch (error) {
        console.error("Error submitting return:", error);
        toast.error(
          getErpRequestErrorMessage(error, "Failed to submit return"),
        );
      } finally {
        setReturnLoading(false);
      }
      return;
    }

    // Submit return for all items (fully return)
    setReturnLoading(true);
    try {
      const success = await submitReturns(items);
      if (success) {
        toast.success("Return submitted successfully. Opening bill for edit.");
        onHide();
        if (onSuccess) onSuccess();
        openEditInvoiceForSale();
      }
      // submitReturns already toasts on failure
    } catch (error) {
      console.error("Error submitting return:", error);
      toast.error(
        getErpRequestErrorMessage(error, "Failed to submit return"),
      );
    } finally {
      setReturnLoading(false);
    }
  };

  // Navigate to sales page with items for sale
  const navigateToSalesAfterReturn = (allItems) => {
    // Take first itemsToSaleCount items for sale
    const itemsForSale = allItems.slice(0, itemsToSaleCount);

    if (itemsForSale.length === 0) {
      onHide();
      if (onSuccess) onSuccess(); // Refresh invoice list
      return;
    }

    // Navigate to sales page with items
    navigate("/sales", {
      state: {
        returnItems: itemsForSale.map((item) => ({
          id: item.id,
          tag_no: item.tag_no || "",
          product_group: item.productGroupName || item.product_group || "",
          gwt: item.gwt || item.gross_weight || 0,
          nwt: item.nwt || item.net_weight || 0,
          totalamt: item.totalamt || item.amount || 0,
        })),
        customer: {
          id: invoice.userId || invoice.user_id || null,
          name: customerDisplay.name,
          mobile: customerDisplay.mobile,
          ...(invoice.userId && users?.[invoice.userId]
            ? users[invoice.userId]
            : {}),
        },
      },
    });

    onHide();
    if (onSuccess) onSuccess(); // Refresh invoice list
  };

  // Handle step 3 - choose which items to sale, then submit returns for others
  const handleProceedToSales = async () => {
    if (!invoice) return;
    const items = invoice.items || [];

    const saleItems = items.filter((it) => saleSelection[it.id]);
    const returnItems = items.filter((it) => !saleSelection[it.id]);

    if (saleItems.length === 0 && returnItems.length === 0) {
      toast.error("Please select at least one option");
      return;
    }

    // Submit returns for the selected return items (if any)
    if (returnItems.length > 0) {
      setReturnLoading(true);
      try {
        const success = await submitReturns(returnItems);
        if (!success) {
          setReturnLoading(false);
          return;
        }
        toast.success(`Return submitted for ${returnItems.length} item(s)`);
      } catch (e) {
        console.error(e);
        toast.error(
          getErpRequestErrorMessage(e, "Failed to submit returns"),
        );
        setReturnLoading(false);
        return;
      }
      setReturnLoading(false);
    }

    // Navigate to sales with sale items (if any)
    if (saleItems.length > 0) {
      navigateToSalesAfterReturn(saleItems);
    } else {
      onHide();
      if (onSuccess) onSuccess();
    }
  };

  if (!invoice) return null;

  return (
    <Modal
      show={show}
      onHide={() => {
        onHide();
        setReturnStep(1);
      }}
      size="xl"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <div className="d-flex align-items-center">
              <span className="fw-semibold">Return Items</span>
              {invoice && (
                <span className="ms-2 text-muted small">
                  | {invoice.voucherNumber}
                </span>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {/* Step indicator with connectors (uses theme colors) */}
              {(() => {
                // Always 2 steps for both fully and partially
                const total = 2;
                const steps = Array.from({ length: total }, (_, i) => i + 1);
                return (
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    {steps.map((s, idx) => (
                      <div key={s} className="d-flex align-items-center">
                        <div
                          className={`rounded-circle d-flex align-items-center justify-content-center ${
                            returnStep >= s
                              ? "bg-primary text-white"
                              : "bg-light text-muted"
                          } ${
                            returnStep === s
                              ? "border border-2 border-white"
                              : ""
                          }`}
                          style={{ width: 26, height: 26, fontSize: 12 }}
                          title={`Step ${s} of ${total}`}
                        >
                          {s}
                        </div>
                        {idx < steps.length - 1 && (
                          <div
                            style={{ width: 80, height: 2 }}
                            className={`${
                              returnStep > s ? "bg-primary" : "bg-light"
                            }`}
                          ></div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Common header info across all steps */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-dark big-font">
              {customerDisplay.name}
              {" - "}
              {customerDisplay.mobile}
            </div>
            <div className="d-flex align-items-center gap-2 text-muted small">
              <strong className="text-dark">Date:</strong>
              <input
                type="date"
                className="form-control form-control-sm d-inline-block"
                style={{ width: "auto", display: "inline-block" }}
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Step 1: Return Order Type Selection */}
        {returnStep === 1 && (
          <div>
            <div className="mb-4">
              <label
                className="form-label fw-semibold mb-3"
                style={{ fontSize: "1rem" }}
              >
                Return Order
              </label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="radio"
                  id="return-fully"
                  label="Fully"
                  name="returnType"
                  value="fully"
                  checked={returnType === "fully"}
                  onChange={() => {
                    setReturnType("fully");
                    setSelectedItemKeysForPartial([]);
                  }}
                  disabled={invoice?.items?.length === 1}
                />
                <Form.Check
                  type="radio"
                  id="return-partially"
                  label="Partially"
                  name="returnType"
                  value="partially"
                  checked={returnType === "partially"}
                  onChange={() => {
                    setReturnType("partially");
                    setSelectedItemKeysForPartial([]);
                  }}
                  disabled={invoice?.items?.length === 1}
                />
              </div>
            </div>

            {/* Items to Return — checkboxes appear only for partial return */}
            <div className="mb-4">
              <label
                className="form-label fw-semibold mb-2"
                style={{ fontSize: "0.95rem" }}
              >
                Items to Return
              </label>
              {returnType === "partially" && allInvoiceItems.length > 1 && (
                <div className="text-muted small mb-2">
                  Check the tags to return to stock. Unchecked tags stay on this
                  bill number.
                  {selectedItemKeysForPartial.length > 0 && (
                    <span className="ms-1">
                      Returning {selectedItemKeysForPartial.length}, keeping{" "}
                      {itemsBeingKept.length} on bill.
                    </span>
                  )}
                </div>
              )}
              <div className="table-responsive border rounded">
                <table className="table table-bordered table-sm mb-0 theme-header-table">
                  <thead>
                    <tr>
                      {returnType === "partially" &&
                        allInvoiceItems.length > 1 && (
                          <th
                            className="px-2 py-2 text-center"
                            style={{
                              fontSize: "0.85rem",
                              fontWeight: "600",
                              width: "56px",
                            }}
                          >
                            Return
                          </th>
                        )}
                      <th
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "600",
                        }}
                      >
                        #
                      </th>
                      <th
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "600",
                        }}
                      >
                        Tag No
                      </th>
                      <th
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "600",
                        }}
                      >
                        Product Group
                      </th>
                      <th
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "600",
                        }}
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allInvoiceItems.length > 0 ? (
                      allInvoiceItems.map((item, idx) => {
                        const isPartial =
                          returnType === "partially" &&
                          allInvoiceItems.length > 1;
                        const itemKey = getReturnItemKey(item, idx);
                        const isChecked =
                          selectedItemKeysForPartial.includes(itemKey);
                        return (
                          <tr
                            key={itemKey}
                            className={
                              isPartial && isChecked ? "table-success" : ""
                            }
                          >
                            {isPartial && (
                              <td
                                className="px-2 py-2 text-center"
                                style={{ fontSize: "0.85rem" }}
                              >
                                <Form.Check
                                  type="checkbox"
                                  id={`return-item-${itemKey}`}
                                  checked={isChecked}
                                  onChange={() =>
                                    togglePartialReturnItem(itemKey)
                                  }
                                />
                              </td>
                            )}
                            <td
                              className="px-2 py-2 text-center"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              className="px-2 py-2"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {item.tag_no || "-"}
                            </td>
                            <td
                              className="px-2 py-2"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {item.productGroupName ||
                                item.product_group_name ||
                                item.product_group ||
                                "-"}
                            </td>
                            <td
                              className="px-2 py-2 text-end"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {parseFloat(
                                item.totalamt || item.amount || 0,
                              ).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={
                            returnType === "partially" &&
                            allInvoiceItems.length > 1
                              ? 5
                              : 4
                          }
                          className="px-2 py-2 text-center text-muted"
                          style={{ fontSize: "0.85rem" }}
                        >
                          No items available to return
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Kept tags — only for partial return */}
            {returnType === "partially" && allInvoiceItems.length > 1 && (
              <div className="mb-4">
                <label
                  className="form-label fw-semibold mb-2"
                  style={{ fontSize: "0.95rem" }}
                >
                  Kept Tags (remain on this bill)
                </label>
                <div className="table-responsive border rounded">
                  <table className="table table-bordered table-sm mb-0 theme-header-table">
                    <thead>
                      <tr>
                        <th
                          className="px-2 py-2 text-center"
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          #
                        </th>
                        <th
                          className="px-2 py-2 text-center"
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          Tag No
                        </th>
                        <th
                          className="px-2 py-2 text-center"
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          Product Group
                        </th>
                        <th
                          className="px-2 py-2 text-center"
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsBeingKept.length > 0 ? (
                        itemsBeingKept.map((item, idx) => (
                          <tr key={item.id || `kept-${idx}`}>
                            <td
                              className="px-2 py-2 text-center"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              className="px-2 py-2"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {item.tag_no || "-"}
                            </td>
                            <td
                              className="px-2 py-2"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {item.productGroupName ||
                                item.product_group_name ||
                                item.product_group ||
                                "-"}
                            </td>
                            <td
                              className="px-2 py-2 text-end"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {parseFloat(
                                item.totalamt || item.amount || 0,
                              ).toFixed(2)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="4"
                            className="px-2 py-2 text-center text-muted"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {selectedItemKeysForPartial.length === 0
                              ? "Check tags above to return — remaining tags will appear here"
                              : "No tags kept — select Fully return if returning all"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="primary"
                onClick={() => {
                  if (
                    returnType === "partially" &&
                    selectedItemKeysForPartial.length === 0
                  ) {
                    toast.error("Please select at least one tag to return");
                    return;
                  }
                  if (
                    returnType === "partially" &&
                    selectedItemKeysForPartial.length >= allInvoiceItems.length
                  ) {
                    toast.error(
                      "Cannot return all tags here — choose Fully return, or leave at least one unchecked to keep on this bill",
                    );
                    return;
                  }
                  if (itemsBeingReturned.length === 0) {
                    toast.error("No items available to return");
                    return;
                  }
                  setReturnStep(2);
                }}
                disabled={
                  (returnType === "partially" &&
                    (selectedItemKeysForPartial.length === 0 ||
                      selectedItemKeysForPartial.length >=
                        allInvoiceItems.length)) ||
                  itemsBeingReturned.length === 0
                }
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Payment */}
        {returnStep === 2 && (
          <div>
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="table-responsive border rounded">
                  <table className="table table-bordered mb-0">
                    <thead>
                      <tr className="bg-light">
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "50px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">No.</span>
                          </div>
                        </th>
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "120px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">Mode</span>
                          </div>
                        </th>
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "180px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">Type</span>
                          </div>
                        </th>
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "150px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">Remarks</span>
                          </div>
                        </th>
                        <th
                          className="p-0 text-center align-middle"
                          style={{ width: "100px" }}
                        >
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "38px" }}
                          >
                            <span className="fw-semibold small">Amt</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {returnPaymentRows.map((row, index) => (
                        <tr key={row.id}>
                          <td className="p-0 align-middle text-center">
                            <div
                              style={{ height: "38px" }}
                              className="d-flex justify-content-center align-items-center"
                            >
                              <span className="small">{index + 1}</span>
                            </div>
                          </td>
                          <td className="p-0 align-middle">
                            <div
                              style={{ height: "38px", paddingLeft: "4px" }}
                              className="d-flex justify-content-start align-items-center"
                            >
                              <Select
                                options={PAYMENT_METHODS}
                                value={PAYMENT_METHODS.find(
                                  (method) => method.value === row.pay_mode
                                )}
                                onChange={(selectedOption) =>
                                  handleReturnPaymentRowChange(
                                    row.id,
                                    "pay_mode",
                                    selectedOption?.value || ""
                                  )
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                maxMenuHeight={160}
                                isSearchable={false}
                                components={{
                                  DropdownIndicator: () => null,
                                  IndicatorSeparator: () => null,
                                }}
                                styles={{
                                  container: (provided) => ({
                                    ...provided,
                                    width: "100%",
                                  }),
                                  control: (provided) => ({
                                    ...provided,
                                    border: "none",
                                    boxShadow: "none",
                                    fontSize: "0.85rem",
                                    minHeight: "38px",
                                    height: "38px",
                                  }),
                                  valueContainer: (provided) => ({
                                    ...provided,
                                    padding: "0 4px",
                                    height: "38px",
                                    justifyContent: "flex-start",
                                  }),
                                  singleValue: (provided) => ({
                                    ...provided,
                                    marginLeft: 0,
                                    marginRight: 0,
                                  }),
                                  input: (provided) => ({
                                    ...provided,
                                    margin: "0",
                                    padding: "0",
                                  }),
                                  indicatorsContainer: () => ({
                                    display: "none",
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    minWidth: "160px",
                                    marginTop: 2,
                                    marginLeft: "-6px",
                                    zIndex: 9999,
                                  }),
                                  menuList: (base) => ({
                                    ...base,
                                    maxHeight: 160,
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                    overflowY: "auto",
                                  }),
                                  option: (base) => ({
                                    ...base,
                                    paddingTop: 6,
                                    paddingBottom: 6,
                                    paddingLeft: 10,
                                    paddingRight: 10,
                                    fontSize: "0.85rem",
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                                placeholder="Select Mode"
                                className="react-select-container"
                                classNamePrefix="react-select"
                              />
                            </div>
                          </td>
                          <td className="p-0 align-middle">
                            <div
                              style={{ height: "38px", paddingLeft: "4px" }}
                              className="d-flex justify-content-start align-items-center"
                            >
                              {(() => {
                                const isCashMode =
                                  String(row.pay_mode).toLowerCase() === "cash";

                                const selectStyles = {
                                  container: (provided) => ({
                                    ...provided,
                                    width: "100%",
                                  }),
                                  control: (provided) => ({
                                    ...provided,
                                    border: "none",
                                    boxShadow: "none",
                                    fontSize: "0.85rem",
                                    minHeight: "38px",
                                    height: "38px",
                                  }),
                                  valueContainer: (provided) => ({
                                    ...provided,
                                    padding: "0 4px",
                                    height: "38px",
                                    justifyContent: "flex-start",
                                  }),
                                  singleValue: (provided) => ({
                                    ...provided,
                                    marginLeft: 0,
                                    marginRight: 0,
                                  }),
                                  input: (provided) => ({
                                    ...provided,
                                    margin: "0",
                                    padding: "0",
                                  }),
                                  indicatorsContainer: () => ({
                                    display: "none",
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    minWidth: "180px",
                                    marginTop: 2,
                                    marginLeft: "-6px",
                                    zIndex: 9999,
                                  }),
                                  menuList: (base) => ({
                                    ...base,
                                    maxHeight: 160,
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                    overflowY: "auto",
                                  }),
                                  option: (base) => ({
                                    ...base,
                                    paddingTop: 6,
                                    paddingBottom: 6,
                                    paddingLeft: 10,
                                    paddingRight: 10,
                                    fontSize: "0.85rem",
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                  }),
                                };

                                const bankOptions = (bankAccountsList || [])
                                  .map((bank) => {
                                    const name =
                                      bank.bank_name ||
                                      bank.bankName ||
                                      bank.name ||
                                      "";
                                    return {
                                      value: String(bank.id),
                                      label: name || `Bank ${bank.id}`,
                                    };
                                  })
                                  .filter((opt) => opt.value);

                                const typeOptionsFromPaymentTypes = (
                                  paymentTypes || []
                                ).map((opt) => ({
                                  value: String(opt.value),
                                  label: opt.label || `Bank ${opt.value}`,
                                }));

                                const resolvedBankOptions =
                                  bankOptions.length > 0
                                    ? bankOptions
                                    : typeOptionsFromPaymentTypes;

                                let options = [];
                                if (isCashMode) {
                                  options = [
                                    { value: "cash", label: "Cash" },
                                  ];
                                } else if (isBanklessPayMode(row.pay_mode)) {
                                  options = [
                                    {
                                      value: row.pay_mode,
                                      label:
                                        PAYMENT_METHODS.find(
                                          (m) => m.value === row.pay_mode,
                                        )?.label || row.pay_mode,
                                    },
                                  ];
                                } else {
                                  options = resolvedBankOptions;
                                }

                                return (
                                  <Select
                                    options={options}
                                    value={
                                      isCashMode
                                        ? options[0]
                                        : options.find(
                                            (option) =>
                                              option.value ===
                                              String(row.type || ""),
                                          ) || null
                                    }
                                    onChange={(selectedOption) =>
                                      handleReturnPaymentRowChange(
                                        row.id,
                                        "type",
                                        selectedOption?.value || ""
                                      )
                                    }
                                    isDisabled={isCashMode}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    maxMenuHeight={160}
                                    isSearchable={false}
                                    components={{
                                      DropdownIndicator: () => null,
                                      IndicatorSeparator: () => null,
                                    }}
                                    styles={selectStyles}
                                    placeholder="Select Type"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    noOptionsMessage={() =>
                                      "No bank accounts available"
                                    }
                                  />
                                );
                              })()}
                            </div>
                          </td>
                          <td className="p-0 align-middle">
                            <div
                              style={{ height: "38px" }}
                              className="d-flex justify-content-center align-items-center"
                            >
                              <input
                                type="text"
                                className="form-control form-control-sm border-0"
                                value={row.remarks}
                                onChange={(e) =>
                                  handleReturnPaymentRowChange(
                                    row.id,
                                    "remarks",
                                    e.target.value
                                  )
                                }
                                placeholder="Remarks"
                                style={{ fontSize: "0.85rem", width: "100%" }}
                              />
                            </div>
                          </td>
                          <td className="p-0 align-middle text-center">
                            <div
                              style={{ height: "38px" }}
                              className="d-flex justify-content-center align-items-center"
                            >
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max={totalReturnAmount}
                                className="form-control form-control-sm border-0 text-end"
                                value={row.amount}
                                onChange={(e) =>
                                  handleReturnPaymentRowChange(
                                    row.id,
                                    "amount",
                                    e.target.value
                                  )
                                }
                                placeholder="0.00"
                                style={{ fontSize: "0.85rem", width: "100%" }}
                                onWheel={(e) => e.target.blur()}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border">
                  <div className="card-body p-3">
                    <div className="row g-3 mb-3">
                      <div className="col-6">
                        <label
                          className="form-label mb-1 fw-semibold"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Final Amount:
                        </label>
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                          Items + {RETURN_GST_PERCENT}% GST
                        </div>
                      </div>
                      <div className="col-6 text-end">
                        <input
                          type="text"
                          className="form-control form-control-sm text-end"
                          readOnly
                          value={String(finalReturnAmount)}
                          style={{
                            fontSize: "0.9rem",
                            padding: "0.375rem 0.5rem",
                          }}
                        />
                      </div>
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-6">
                        <label
                          className="form-label mb-1 fw-semibold"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Paid Amount:
                        </label>
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                          Paid on this bill till now
                        </div>
                      </div>
                      <div className="col-6 text-end">
                        <input
                          type="text"
                          className="form-control form-control-sm text-end"
                          readOnly
                          value={String(billPaidAmount)}
                          style={{
                            fontSize: "0.9rem",
                            padding: "0.375rem 0.5rem",
                          }}
                        />
                      </div>
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-6">
                        <label
                          className="form-label mb-1 fw-semibold"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Remaining Amount:
                        </label>
                      </div>
                      <div className="col-6 text-end">
                        <input
                          type="text"
                          className="form-control form-control-sm text-end"
                          readOnly
                          value={String(billRemainingAmount)}
                          style={{
                            fontSize: "0.9rem",
                            padding: "0.375rem 0.5rem",
                          }}
                        />
                      </div>
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-6">
                        <label
                          className="form-label mb-1 fw-semibold"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Return Amount:
                        </label>
                      </div>
                      <div className="col-6 text-end">
                        <input
                          type="text"
                          className="form-control form-control-sm text-end"
                          readOnly
                          value={String(returnAmountDue)}
                          style={{
                            fontSize: "0.9rem",
                            padding: "0.375rem 0.5rem",
                            color: "#28a745",
                            fontWeight: "600",
                          }}
                        />
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-12">
                        <label
                          className="form-label mb-1 fw-semibold"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Remarks:
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={returnRemarks}
                          onChange={(e) => setReturnRemarks(e.target.value)}
                          placeholder="Enter remarks..."
                          style={{
                            fontSize: "0.9rem",
                            padding: "0.375rem 0.5rem",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" onClick={() => setReturnStep(1)}>
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleReturnSave}
                disabled={returnLoading}
              >
                {returnLoading ? "Processing..." : "Save"}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ReturnModal;
